// src/pages/components/ProjectDetailsDetailsCard.js
import { useMemo, useState } from 'react';
import axios from 'axios';
import { Box, Chip, Menu, MenuItem, CircularProgress } from '@mui/material';

import { formatStatusLabel } from '../helpers';
import { Paper, Stack, Typography } from '@mui/material';

import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';

const InlineStatusChip = ({ projectId, status, meta, onUpdated }) => {
  const STATUS_OPTIONS = [
    { value: 'in_progress', label: 'In progress', color: 'primary' },
    { value: 'finished', label: 'Finished', color: 'success' },
    { value: 'archived', label: 'Archived', color: 'default' },
  ];

  const [anchorEl, setAnchorEl] = useState(null);
  const [saving, setSaving] = useState(false);
  const open = Boolean(anchorEl);

  const label = useMemo(() => formatStatusLabel(status), [status]);

  const handleOpen = (e) => {
    if (saving) return;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleChangeStatus = async (nextStatus) => {
    if (nextStatus === status) {
      handleClose();
      return;
    }

    handleClose();
    setSaving(true);

    try {
      const res = await axios.patch(`/api/v1/project/${projectId}/details`, { status: nextStatus });

      const doc = res?.data?.data?.project || res?.data?.data || res?.data;

      if (onUpdated && doc) onUpdated(doc);
    } catch (err) {
      // optional: toast here
      console.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Chip
        label={
          saving ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} />
              Updating…
            </Box>
          ) : (
            label
          )
        }
        color={meta.chipColor}
        variant={meta.variant}
        size="small"
        sx={{ fontWeight: 600, cursor: saving ? 'default' : 'pointer', p: '15px 10px' }}
        onClick={handleOpen}
      />

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {STATUS_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} selected={opt.value === status} onClick={() => handleChangeStatus(opt.value)}>
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ProjectDetailsDetailsCard = ({ meta, status, created, updated, projectId, onProjectUpdated }) => {
  return (
    <Paper variant="outlined" sx={{ p: 2, position: 'relative', mb: 5 }}>
      <Typography
        color="text.secondary"
        variant="caption"
        sx={{
          position: 'absolute',
          top: -8,
          left: 12,
          backgroundColor: 'background.paper',
          px: 0.5,
          fontWeight: 700,
        }}
      >
        Project Details
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} alignItems="center" justifyContent="space-between">
        {/* Created */}
        <Stack direction="row" spacing={0.75} alignItems="center">
          <EventOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Created:{' '}
            <Box component="span" color="text.primary">
              {created?.absolute || '—'}
            </Box>
          </Typography>
        </Stack>

        {/* Updated */}
        <Stack direction="row" spacing={0.75} alignItems="center">
          <UpdateOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Last modified:{' '}
            <Box component="span" color="text.primary">
              {updated?.relative || '—'}
            </Box>
          </Typography>
        </Stack>

        <InlineStatusChip projectId={projectId} status={status} meta={meta} onUpdated={onProjectUpdated} />
      </Stack>
    </Paper>
  );
};

export default ProjectDetailsDetailsCard;
